import React from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DirectoryRecordDialog } from '../../../components/admin/directory/DirectoryKit';
import { SourceTag, StatusChip } from '../../../components/status/StatusChip';
import { formatDay, formatMoney } from '@/domain/warehouse';
import {
  purchaseOrderErrorMessage,
  purchaseOrderRecordKeys,
  searchOpenPurchaseOrders,
  type OpenPurchaseOrderOption,
} from '@/data/purchaseOrderRecord';
import { localDay, purchaseOrderHref } from './purchaseOrderRecordModel';
import './PackingSlipOrderPicker.css';

const SEARCH_DELAY_MS = 250;
const RESULT_LIMIT = 25;

export type PackingSlipOrderPickerProps = {
  open: boolean;
  onClose: () => void;
  /** Called with the chosen order; without it the picker opens its Receiving tab and starts the scan. */
  onPick?: (poId: string) => void;
};

function optionMeta(option: OpenPurchaseOrderOption): string {
  const expected = option.expectedAt ? formatDay(localDay(option.expectedAt)) : null;
  const ordered = option.orderedAt ? formatDay(localDay(option.orderedAt)) : null;
  return [
    option.vendorName ?? 'No vendor',
    option.siteName,
    expected ? `expected ${expected}` : ordered ? `ordered ${ordered}` : null,
    formatMoney(option.totalAmount),
  ].filter(Boolean).join(' · ');
}

/**
 * The Warehouse hero's "Scan packing slip": pick the open purchase order the slip belongs to, then
 * its Receiving tab opens with the scan (`?scan=1`). Orders are searched on the server, at most 25
 * at a time; cross-rentals and labor orders are left out.
 */
export default function PackingSlipOrderPicker({ open, onClose, onPick }: PackingSlipOrderPickerProps) {
  if (!open) return null;
  return <PackingSlipOrderPickerPanel onClose={onClose} onPick={onPick} />;
}

function PackingSlipOrderPickerPanel({ onClose, onPick }: { onClose: () => void; onPick?: (poId: string) => void }) {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [term, setTerm] = React.useState('');
  const [debounced, setDebounced] = React.useState('');

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [term]);

  React.useEffect(() => {
    // The dialog takes focus first; then the search field.
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, []);

  const ordersQuery = useQuery({
    queryKey: purchaseOrderRecordKeys.openOrders(debounced.toLocaleLowerCase()),
    queryFn: () => searchOpenPurchaseOrders(debounced),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const results = ordersQuery.data ?? [];
  const searching = term.trim() !== debounced || ordersQuery.isFetching;

  function handleTermChange(event: React.ChangeEvent<HTMLInputElement>) {
    setTerm(event.target.value);
  }

  function pick(poId: string) {
    onClose();
    if (onPick) onPick(poId);
    else navigate(purchaseOrderHref(poId, 'receiving', 'scan=1'));
  }

  return (
    <DirectoryRecordDialog
      title="Scan a packing slip"
      subtitle="Pick the purchase order the slip belongs to. Its Receiving tab opens and asks for the slip."
      size="md"
      onClose={onClose}
      className="packing-slip-picker"
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>}
    >
      <div className="directory-stack">
        <label className="directory-field">
          <span className="directory-field__label">Find an open purchase order</span>
          <input
            ref={inputRef}
            type="search"
            value={term}
            onChange={handleTermChange}
            placeholder="PO number, R2 number or vendor"
            autoComplete="off"
          />
        </label>
        <div className="packing-slip-picker__status" role="status" aria-live="polite">
          {searching ? 'Searching…' : results.length === RESULT_LIMIT ? `Showing the ${RESULT_LIMIT} newest matches. Type to narrow them.` : ''}
        </div>
        {ordersQuery.isError ? (
          <p className="packing-slip-picker__error" role="alert">
            {purchaseOrderErrorMessage(ordersQuery.error, 'Purchase orders could not be searched. Try again.')}
          </p>
        ) : null}
        {!ordersQuery.isError && !ordersQuery.isPending && !results.length ? (
          <p className="packing-slip-picker__empty">
            {debounced ? 'No open purchase order matches. Check the number, or search by vendor.' : 'No purchase orders are waiting for deliveries.'}
          </p>
        ) : null}
        {results.length ? (
          <ul className="packing-slip-picker__results" aria-label="Open purchase orders">
            {results.map((option) => (
              <li key={option.id}>
                <button type="button" className="packing-slip-picker__option" onClick={() => pick(option.id)}>
                  <span className="packing-slip-picker__identity">
                    <strong>{option.docNo ?? 'Purchase order'}</strong>
                    <SourceTag source={option.isR2 ? 'r2' : 'portal'} />
                    <StatusChip kind="purchaseOrder" status={option.status} size="sm" />
                  </span>
                  <span className="packing-slip-picker__meta">{optionMeta(option)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </DirectoryRecordDialog>
  );
}
